import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import PaperDraggable from "../../../../../../../../components/PaperDraggable";
import { useEffect, useMemo, useState } from "react";
import { formatarDecimalNumber, formatarInputValorMonetárioSemPrefixo, formatarMonetárioParaNúmero } from "../../../../../../../../helpers/masks";
import { useFormContext } from "react-hook-form";
import { TCondicaoParcelas } from "../../../../../../../../components/SelecaoCondicaoParcelamento";
import { IDataCondicoesPagamentoComercial, IFormaRecebimentoComercial } from "../../../../../../../../types/api/comercial/pesquisar-condicoes-pagamento";
import FormAPrazoAPI from "./FormAPrazoAPI";
import { enqueueSnackbar } from "notistack";
import { IDataPublicos } from "../../../../../../../../types/api/geral/pesquisar-publicos";

export default function DialogCadastrarValor({
    open,
    value,
    cliente,
    handleClose,
}: DialogCadastrarValorProps) {

    const { getValues, setValue, watch } = useFormContext()

    const editModalMode = useMemo(() => value !== null, [value])

    const parcelamentoMaximo = watch('parcelamento_maximo')
    const condicaoPagamento: IDataCondicoesPagamentoComercial = getValues('condicao_pagamento')
    const formasRecebimento: IFormaRecebimentoComercial[] = getValues('condicao_pagamento.formas_recebimento')

    const valorRestante = watch('valor_restante')

    const [internForm, setInternForm] = useState<IInternFormCheckout>({
        condicao_pagamento: condicaoPagamento,
        forma_recebimento: null,
        valor: formatarDecimalNumber(valorRestante),
        pagamento: defaultValuesPagamento,
        parcelamento_maximo: parcelamentoMaximo,
        tipo_publico: cliente.tipo_publico,
        codigo_publico: cliente.codigo_publico,
        diaVencimentoPadrao: cliente.dia_vencimento_padrao || 20,
        status: "Aguardando pagamento",
        pagamento_id: 0
    })


    //funções manipuladoras
    const handleChangeValues = (prop: keyof IInternFormCheckout, value: any) => {
        setInternForm(prevState => {
            return ({
                ...prevState,
                [prop]: value
            })
        })
    }

    const handleSubmitForm = () => {
        const params = internForm;
        const arrRecb = getValues('valoresAdicionados');


        if (params.valor.includes('-')) {
            return enqueueSnackbar('O valor não pode ser negativo', { variant: 'warning' });
        }
        if (editModalMode) arrRecb.splice(value, 1, params);
        if (!editModalMode) arrRecb.push(params);

        setValue('valoresAdicionados', arrRecb);
        handleClose();
    }
    //
    const precisaParcelas = useMemo(() => {
        if (!internForm.forma_recebimento?.flg_possui_parcelas) return false;
        return true;
    }, [internForm])


    //valores importantes
    const btnDisabled = useMemo(() => {
        const camposBaseFilled = Boolean(internForm.forma_recebimento) && formatarMonetárioParaNúmero(internForm.valor) > 0;
        const quantidadeParcelas = internForm.pagamento.qtdParcelas > 0 && internForm.pagamento.parcelas.length === internForm.pagamento.qtdParcelas;
        const precisaQuantidade = precisaParcelas ? quantidadeParcelas : true
        return !(camposBaseFilled && precisaQuantidade);
    }, [internForm, precisaParcelas])


    //

    useEffect(() => {
        if (value !== null) {
            const formValues = getValues('valoresAdicionados');
            const raw = formValues[value] as Partial<IInternFormCheckout> & { pagamento?: any };

            const formaSelecionada = raw?.forma_recebimento?.id
                ? formasRecebimento.find(i => i.id === (raw as any).forma_recebimento.id) || null
                : null;

            const parcelasNormalizadas = Array.isArray(raw?.pagamento?.parcelas)
                ? raw.pagamento.parcelas.map((p: any) => ({
                    valor: Number(p.valor),
                    vencimento: p.vencimento,
                }))
                : [];

            const pagamentoNormalizado = {
                maximo_parcelas: Number(raw?.pagamento?.maximo_parcelas ?? condicaoPagamento?.parametros.maximo_parcelas ?? 0),
                taxa: Number(raw?.pagamento?.taxa ?? condicaoPagamento?.taxa ?? 0),
                parcelas: parcelasNormalizadas,
                qtdParcelas: Number(raw?.pagamento?.qtdParcelas ?? parcelasNormalizadas.length ?? 0),
                intervalo: Number(raw?.pagamento?.intervalo ?? formaSelecionada?.intervalo ?? 0),
                condicao_pagamento_id: Number(condicaoPagamento?.id || 0),
                forma_recebimento_id: Number(formaSelecionada?.id || 0),
                tipo_publico: cliente.tipo_publico,
                codigo_publico: cliente.codigo_publico,
                diaVencimentoPadrao: cliente.dia_vencimento_padrao || 20,
            } as TCondicaoParcelas;

            setInternForm({
                condicao_pagamento: condicaoPagamento,
                forma_recebimento: formaSelecionada,
                valor: raw?.valor ?? formatarDecimalNumber(valorRestante),
                pagamento: pagamentoNormalizado,
                parcelamento_maximo: parcelamentoMaximo,
                tipo_publico: cliente.tipo_publico,
                codigo_publico: cliente.codigo_publico,
                diaVencimentoPadrao: cliente.dia_vencimento_padrao || 20,
                status: raw?.status ?? "Aguardando pagamento",
                pagamento_id: Number((raw as any)?.pagamento_id || 0),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperComponent={PaperDraggable}
            aria-labelledby="draggable-dialog-title"
            scroll="body"
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                Adicionar valor
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={2} mt={1}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <FormControl fullWidth required>
                            <InputLabel>Forma de recebimento</InputLabel>
                            <Select
                                key={internForm.forma_recebimento?.id}
                                value={internForm.forma_recebimento?.id || ""}
                                onChange={(event) => {
                                    const selectedOption = formasRecebimento.find(i => i.id === event.target.value);
                                    handleChangeValues("forma_recebimento", selectedOption);
                                    handleChangeValues("pagamento", {
                                        ...defaultValuesPagamento,
                                        intervalo: selectedOption?.intervalo ?? 1
                                    });
                                }}
                                label="Forma de Pagamento"
                            >
                                {formasRecebimento.map(forma => (
                                    <MenuItem key={forma.id} value={forma.id}>
                                        {forma.nome}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label='Valor'
                            type="text"
                            value={internForm.valor}
                            onChange={(e) => {
                                const formattedValue = formatarInputValorMonetárioSemPrefixo(e.target.value)
                                handleChangeValues("valor", formattedValue)
                            }}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography color={'#000'}>R$</Typography>
                                        </InputAdornment>
                                    )
                                }
                            }}
                        />
                    </Grid>
                    {
                        (internForm.forma_recebimento &&
                            internForm.forma_recebimento.maximo_parcelas > 0) &&
                        precisaParcelas &&
                        <Grid size={{ xs: 12 }}>
                            <FormAPrazoAPI {...{ handleChangeValues }} values={internForm} />
                        </Grid>
                    }

                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                >
                    Cancelar
                </Button>
                <Button
                    autoFocus
                    variant="contained"
                    onClick={handleSubmitForm}
                    disabled={btnDisabled}
                >
                    Confirmar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface DialogCadastrarValorProps {
    open: boolean;
    value: number | null;
    subTotal: number;
    cliente: IDataPublicos;
    handleClose: () => void;
}

export interface IInternFormCheckout {
    condicao_pagamento: IDataCondicoesPagamentoComercial,
    forma_recebimento: IFormaRecebimentoComercial | null,
    valor: string,
    pagamento: TCondicaoParcelas,
    parcelamento_maximo: number
    tipo_publico: string,
    codigo_publico: string
    diaVencimentoPadrao: number
    status: string
    pagamento_id: number
}

const defaultValuesPagamento = {
    qtdParcelas: 0,
    maximo_parcelas: 0,
    taxa: 0,
    parcelas: [],
    intervalo: 1
}